/* cd-motion.js — Capa de MOVIMIENTO portada a Horizon (vanilla, sin React).
   ============================================================================
   Devuelve la vida que se perdió: scroll suave (Lenis) + revelados al hacer
   scroll (GSAP/ScrollTrigger/SplitText) + hilos que se dibujan + parallax.

   CLAVE: NO depende de las clases internas del prototipo. Engancha por ATRIBUTOS
   data-cd-* que tú pones en el markup de Horizon. Así funciona aunque el tema
   use otros nombres de clase.

   DEPENDENCIAS (cargar ANTES de este archivo, en el <head> o antes de </body>):
     gsap.min.js · ScrollTrigger.min.js · SplitText.min.js · lenis (umd)
   En Horizon: súbelos a Assets y cárgalos con {{ 'gsap.min.js' | asset_url }} etc.
   (GSAP + ScrollTrigger son gratis; SplitText es gratis desde 2024; Lenis es MIT.)

   CÓMO ETIQUETAR EL MARKUP (lo único que el agente debe hacer):
     · Sección que revela su contenido:  <section data-cd-reveal> … </section>
     · Dentro de ella, marca los roles (todos opcionales):
         data-cd-label    → kicker mono (entra con fade + y)
         data-cd-title    → titular (entra línea por línea, máscara overflow)
         data-cd-support  → bajada / texto de apoyo
         data-cd-item     → tarjetas/elementos repetidos (entran con stagger)
     · Hilo divisor que se dibuja:  <div data-cd-thread><span class="line">…</span></div>
     · Imagen con parallax de profundidad:  <div data-cd-parallax> … </div>
     · Hero (revela al cargar, sin esperar scroll):  <section data-cd-hero> … </section>
       (sus hijos usan los mismos data-cd-label/title/support)

   ACCESIBILIDAD: si prefers-reduced-motion está activo, NADA corre (scroll nativo,
   contenido visible al instante). Si falta GSAP, se quita el pre-oculto y se ve todo.

   VELOCIDAD (23-oct-2026) — por qué el arranque es "perezoso":
     Antes, al cargar, se preparaban TODAS las secciones de golpe: SplitText partía
     cada titular y gsap.set fijaba cada estado inicial, alternando lecturas y
     escrituras de estilo. Medido en la traza (4x CPU · Slow 4G): 211 recálculos de
     estilo forzados = UNA tarea de 6,9 s con la página congelada.
     Ahora cada sección se prepara y anima solo cuando llega al 80% de la pantalla
     (o al instante si ya está a la vista). Misma coreografía, sin el bloqueo.
     Reglas: al arrancar NO se lee estilo ni se parte texto, solo se ponen clases;
     y las lecturas de cada sección se agrupan en un solo pase (prime) antes de escribir.
   ========================================================================== */
(function () {
  "use strict";

  var docEl = document.documentElement;
  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Pre-oculta los objetivos SOLO si vamos a animar (evita FOUC). Se revierte si algo falla.
  if (!REDUCED) docEl.classList.add("cd-js-motion");

  if (REDUCED) return; // scroll nativo + todo visible

  // Plan B global: si algo se rompe, se ve TODO (nunca dejar contenido oculto).
  function mostrarTodo() {
    var w = document.querySelectorAll(".cd-rv-wait");
    for (var i = 0; i < w.length; i++) w[i].classList.remove("cd-rv-wait");
    docEl.classList.remove("cd-js-motion");
  }

  function boot() {
    try { arrancar(); }
    catch (err) {
      console.warn("[cd-motion] Falló el arranque — se muestra todo sin animación.", err);
      mostrarTodo();
    }
  }

  function arrancar() {
    if (!window.gsap || !window.ScrollTrigger) {
      console.warn("[cd-motion] GSAP/ScrollTrigger no disponibles — se omite la coreografía.");
      mostrarTodo();
      return;
    }
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    var SplitText = window.SplitText || null;
    gsap.registerPlugin(ScrollTrigger);
    if (SplitText) gsap.registerPlugin(SplitText);

    var MOBILE =
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) ||
      (window.matchMedia && window.matchMedia("(hover: none), (pointer: coarse)").matches);
    var RV = MOBILE ? 0.78 : 1;   // factor de duración
    var PX = MOBILE ? 0.45 : 1;   // factor de parallax

    function qsa(scope, sel) { return Array.prototype.slice.call(scope.querySelectorAll(sel)); }

    // Lee el transform de varios elementos en UN solo pase (un único cálculo de estilo).
    // Después GSAP solo escribe: así se evita el vaivén leer-escribir-leer que congelaba la carga.
    function prime(els) {
      for (var i = 0; i < els.length; i++) { if (els[i]) gsap.getProperty(els[i], "y"); }
    }

    // ¿Ya cargó la fuente del titular? Partir en líneas con la fuente de respaldo deja saltos
    // de línea equivocados cuando llega la fuente real; en ese caso el titular entra en bloque.
    function fuenteLista(el) {
      if (!document.fonts || !document.fonts.check) return true;
      try {
        var cs = window.getComputedStyle(el);
        return document.fonts.check(cs.fontStyle + " " + cs.fontWeight + " " + cs.fontSize + " " + cs.fontFamily);
      } catch (e) { return true; }
    }

    // ── Titular en líneas, cada línea en máscara overflow:hidden ──
    function splitTitle(el) {
      if (!SplitText) return null;
      try {
        var split = new SplitText(el, { type: "lines", linesClass: "cd-rv-line" });
        var bad = false;
        for (var k = 0; k < split.lines.length; k++) {
          if (split.lines[k].scrollWidth > el.clientWidth + 2) { bad = true; break; }
        }
        if (bad) { if (split.revert) split.revert(); return null; }
        split.lines.forEach(function (line) {
          var mask = document.createElement("span");
          mask.className = "cd-rv-mask";
          line.parentNode.insertBefore(mask, line);
          mask.appendChild(line);
        });
        return split;
      } catch (e) { return null; }
    }
    function unmask(el) {
      if (!el) return;
      var masks = el.querySelectorAll ? el.querySelectorAll(".cd-rv-mask") : [];
      for (var i = 0; i < masks.length; i++) masks[i].style.overflow = "visible";
    }

    // ── Red de seguridad: si lleva 1.5s visible y no terminó, fuerza el final ──
    function safetyNet(el, finish) {
      if (!el || !("IntersectionObserver" in window)) return;
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) setTimeout(function () { finish(true); }, 1500); });
      }, { threshold: 0 });
      io.observe(el);
    }

    // ── 1 · Lenis (scroll suave) ──
    function initLenis() {
      if (!window.Lenis) { console.warn("[cd-motion] Lenis no disponible — scroll nativo, revelados activos."); return; }
      var lenis = new window.Lenis({
        duration: 1.2, smoothWheel: true,
        easing: function (t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); },
        lerp: 0.1
      });
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
      window.__cdLenis = lenis;
    }

    // ── 2 · Revelado de una sección [data-cd-reveal] o [data-cd-hero] ──
    // Se llama en el MOMENTO de animar (hero: al cargar; resto: al llegar a la pantalla).
    function revelar(scope, isHero, onDone) {
      var label   = scope.querySelector("[data-cd-label]");
      var titleEl = scope.querySelector("[data-cd-title]");
      var support = scope.querySelector("[data-cd-support]");
      var cards   = qsa(scope, "[data-cd-item]");

      // LCP (23-oct-2026): en el hero el titular ES el elemento LCP. No se parte en líneas,
      // no se oculta ni se mueve: queda pintado desde el primer cuadro (el resto sí anima).
      var animaTitulo = !!titleEl && !isHero;
      var split = (animaTitulo && fuenteLista(titleEl)) ? splitTitle(titleEl) : null;

      // Un solo pase de lectura para todo lo que se va a mover…
      prime([label, support, (animaTitulo && !split) ? titleEl : null].concat(cards, split ? split.lines : []));

      // …y a partir de aquí solo escrituras (fromTo pinta el estado inicial al instante).
      var tl = gsap.timeline({
        delay: isHero ? 0.2 : 0,
        onComplete: function () { unmask(titleEl); if (onDone) onDone(); }
      });
      var tStart = isHero ? 0.18 : 0.05;
      if (label) tl.fromTo(label, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.5 * RV, ease: "power2.out" }, 0);
      if (animaTitulo) {
        if (split) {
          tl.fromTo(split.lines, { yPercent: 110 }, { yPercent: 0, duration: 0.8 * RV, ease: "power4.out", stagger: 0.08 * RV }, tStart);
          titleEl.style.opacity = "1";
        } else {
          tl.fromTo(titleEl, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8 * RV, ease: "power4.out" }, tStart);
        }
        titleEl.classList.remove("cd-rv-wait");
      }
      if (support) tl.fromTo(support, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.6 * RV, ease: "power2.out" }, (isHero ? 0.38 : 0.25) * RV);
      if (cards.length) tl.fromTo(cards, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.6 * RV, ease: "power3.out", stagger: 0.08 * RV }, (isHero ? 0.5 : 0.3) * RV);
      return tl;
    }

    // ── 3 · Secciones: al cargar SOLO se marca el titular con una clase (sin leer estilos) ──
    function prepararSeccion(s) {
      var titleEl = s.querySelector("[data-cd-title]");
      if (titleEl) titleEl.classList.add("cd-rv-wait");
      var tl = null, finished = false;
      function done() { finished = true; }
      function go() {
        if (tl || finished) return tl;
        try { tl = revelar(s, false, done); }
        catch (err) {
          console.warn("[cd-motion] Sección mostrada sin animación:", err);
          if (titleEl) titleEl.classList.remove("cd-rv-wait");
          s.classList.add("cd-rv-fail");
          finished = true;
        }
        return tl;
      }
      // Sección que ya quedó ARRIBA de la pantalla (recarga a mitad de página, ancla):
      // no tiene sentido animarla fuera de vista → se muestra tal cual, sin costo.
      function mostrarSinAnimar() {
        if (tl || finished) return;
        finished = true;
        if (titleEl) titleEl.classList.remove("cd-rv-wait");
        s.classList.add("cd-rv-done");
      }
      ScrollTrigger.create({ trigger: s, start: "top 80%", once: true, onEnter: function (self) {
        if (self && self.progress >= 1) mostrarSinAnimar(); else go();
      } });
      safetyNet(titleEl || s, function () {
        if (finished) return;
        var t = go();
        if (t && !finished) t.progress(1);
      });
    }

    // ── 4 · Hilos divisores que se dibujan ──
    function buildThreads() {
      qsa(document, "[data-cd-thread]").forEach(function (t) {
        var line = t.querySelector(".line") || t.firstElementChild;
        if (!line) return;
        line.classList.add("cd-rv-wait");
        var tl = null, hecho = false;
        function go() {
          if (tl) return tl;
          line.style.opacity = "0";
          line.style.setProperty("--draw", "0");
          line.classList.remove("cd-rv-wait");
          tl = gsap.timeline({ onComplete: function () { hecho = true; } })
            .to(line, { "--draw": 1, duration: 0.9, ease: "power3.inOut" }, 0)
            .to(line, { opacity: 1, duration: 0.5, ease: "power2.out" }, 0.3);
          return tl;
        }
        ScrollTrigger.create({ trigger: t, start: "top 88%", once: true, onEnter: function (self) {
          if (self && self.progress >= 1 && !tl) { line.classList.remove("cd-rv-wait"); hecho = true; } // ya pasó: se ve dibujado
          else go();
        } });
        safetyNet(t, function () { if (!hecho) go().progress(1); });
      });
    }

    // ── 5 · Parallax de profundidad ──
    function buildParallax() {
      var els = qsa(document, "[data-cd-parallax]");
      prime(els);
      els.forEach(function (el) {
        var amp = 6 * PX;
        gsap.fromTo(el, { y: (-amp) + "%" }, {
          y: amp + "%", ease: "none",
          scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: 1 }
        });
      });
    }

    // ── Arranque ──
    initLenis();
    qsa(document, "[data-cd-hero]").forEach(function (h) {
      try { revelar(h, true, null); }
      catch (err) { console.warn("[cd-motion] Hero mostrado sin animación:", err); h.classList.add("cd-rv-fail"); }
    });
    qsa(document, "[data-cd-reveal]").forEach(prepararSeccion);
    buildThreads();
    buildParallax();
    ScrollTrigger.refresh();

    // Re-medir tras cargar fuentes (Fraunces cambia los saltos de línea).
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    window.addEventListener("load", function () { ScrollTrigger.refresh(); });
    var rt; window.addEventListener("resize", function () { clearTimeout(rt); rt = setTimeout(function () { ScrollTrigger.refresh(); }, 220); });
    window.__cdMotionStarted = true;
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();

/* ── VELOCIDAD 23-oct-2026 (noche) · ANIMACIONES CSS EN PAUSA FUERA DE PANTALLA ──
   Los degradados que fluyen y los pulsos de brillo (background-position, box-shadow)
   no los mueve la tarjeta gráfica: obligan al hilo principal a repintar en CADA cuadro,
   aunque estén fuera de la pantalla. En la traza de la Home, con la página quieta, el
   hilo principal pasaba el 88% del tiempo repintando (4x CPU): eso es lo que demora la
   respuesta a un toque (INP). Aquí cada sección pone en pausa sus animaciones CSS
   mientras no se ve y las reanuda 150 px antes de entrar. En pantalla todo se ve igual.
   La cabecera no se toca (siempre está a la vista). Si algo falla, no pausa nada. */
(function () {
  "use strict";
  if (!("IntersectionObserver" in window)) return;
  function iniciar() {
    try {
      var secciones = document.querySelectorAll("main .shopify-section, footer .shopify-section");
      if (!secciones.length) return;
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          // Un contenedor sin alto (p. ej. solo un elemento flotante) nunca se pausa.
          var fuera = !e.isIntersecting && e.boundingClientRect.height > 1;
          e.target.classList.toggle("cd-anim-pausa", fuera);
        });
      }, { rootMargin: "150px 0px 150px 0px", threshold: 0 });
      for (var i = 0; i < secciones.length; i++) io.observe(secciones[i]);
    } catch (err) { /* sin pausa: la página queda exactamente como antes */ }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", iniciar);
  else iniciar();
})();